import React, {createContext, DependencyList, ReactNode, useCallback, useEffect, useRef, useState} from 'react';
import {HubConnection, HubConnectionBuilder, HubConnectionState, IHttpConnectionOptions} from '@microsoft/signalr';

export interface SignalRHubFactoryOptions {
  name: string;
}

export interface ContextValue {
  connection: HubConnection | null;
  lastStartTime: number;
}

export interface SignalRHub {
  Context: React.Context<ContextValue | null>;
  Provider: (props: ProviderProps) => React.ReactNode;
  name: string;
}

export interface ProviderProps extends IHttpConnectionOptions {
  connectEnabled?: boolean;
  dependencies?: DependencyList;
  onError?: (error?: Error) => void;
  url: string;
  children: ReactNode | ReactNode[];
  onBeforeClose?: (connection: HubConnection) => Promise<void> | void;
  onClosed?: (error?: Error) => void;
  onOpen?: (connection: HubConnection) => void;
  onReconnect?: (connection: HubConnection) => void;
  automaticReconnect?: boolean | number[];
}

export const signalRHubFactory = (options: SignalRHubFactoryOptions): SignalRHub => {
  const Context = createContext<ContextValue | null>(null);

  function Provider({
    url,
    connectEnabled = true,
    automaticReconnect = true,
    children,
    dependencies = [],
    accessTokenFactory,
    onError,
    onOpen,
    onReconnect,
    onClosed,
    onBeforeClose,
    logger,
    ...rest
  }: ProviderProps) {
    const [connection, setConnection] = useState<HubConnection | null>(null);
    const [lastStartTime, setLastStartTime] = useState(0);
    const onErrorRef = useEvent(onError);
    const accessTokenFactoryRef = useEvent(accessTokenFactory);
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    const clear = useRef(() => {});

    function refreshConnection() {
      if (!connectEnabled) {
        return;
      }

      const connection = createConnection(
        url,
        {
          accessTokenFactory: () => accessTokenFactoryRef?.() || '',
          logger,
          ...rest,
        },
        automaticReconnect,
      );

      connection.onreconnected(() => onReconnect?.(connection));

      connection.onreconnecting((error) => onErrorRef?.(error));

      connection.onclose((error) => {
        onClosed?.(error);
      });

      async function checkForStart() {
        if (!isConnectionConnecting(connection)) {
          try {
            await connection.start();
            setLastStartTime(Date.now());
            onOpen?.(connection);
          } catch (err) {
            onErrorRef?.(err as Error);
          }
        }
      }

      void checkForStart();

      const checkInterval = setInterval(checkForStart, 6000);

      function onBeforeunload() {
        if (isConnectionConnecting(connection)) {
          connection.stop();
        }
      }

      window?.addEventListener?.('beforeunload', onBeforeunload);

      clear.current = async () => {
        clearInterval(checkInterval);
        await onBeforeClose?.(connection);
        void connection.stop();
        window?.removeEventListener?.('beforeunload', onBeforeunload);
      };

      setConnection(connection);
    }

    useState(() => {
      refreshConnection();
    });

    useEffect(() => {
      refreshConnection();
      return () => {
        clear.current();
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [connectEnabled, url, ...dependencies]);

    return <Context.Provider value={{connection, lastStartTime}}>{children}</Context.Provider>;
  }

  return {
    Context,
    Provider,
    name: options.name,
  };
};

const useEvent = <T extends undefined | ((...args: any[]) => any)>(prop: T): T => {
  const ref = useRef<T>(prop);
  if (ref.current !== prop) {
    ref.current = prop;
  }

  const callback = useCallback((...args: any[]) => {
    return ref.current!(...args);
  }, []) as T;

  return prop ? callback : prop;
};

function isConnectionConnecting(connection: HubConnection) {
  return (
    connection.state === HubConnectionState.Connected ||
    connection.state === HubConnectionState.Reconnecting ||
    connection.state === HubConnectionState.Connecting
  );
}

function createConnection(
  url: string,
  transportType: IHttpConnectionOptions,
  automaticReconnect: boolean | number[] = true,
) {
  let connectionBuilder = new HubConnectionBuilder().withUrl(url, transportType);

  if (automaticReconnect) {
    if (Array.isArray(automaticReconnect)) {
      connectionBuilder = connectionBuilder.withAutomaticReconnect(automaticReconnect);
    } else {
      connectionBuilder = connectionBuilder.withAutomaticReconnect();
    }
  }

  if (transportType.logger) {
    connectionBuilder = connectionBuilder.configureLogging(transportType.logger);
  }

  const connection = connectionBuilder.build();

  return connection;
}
